import React from 'react';
import FormRendererV2 from '../../components/FormRenderer/FormRenderer';
import { FormSchema } from '../../components/FormRenderer/types';
import { Control, UseFormSetValue } from 'react-hook-form';
import ParentPostPublishAccess from './ParentPostPublishAccess';
import RoleFeatureMapping from './RoleFeatureMapping';
import { initialSchoolConfigProps } from './types';
import Icons from '../../components/icons';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography
} from '@mui/material';
import CustomFieldMapping from './CustomFieldMapping';
import { RootState } from '../../redux/store';
import { useSelector } from 'react-redux';

interface OrgSectionProps {
  sectionName: string;
  sectionSchema: FormSchema;
  control: Control<any>;
  handleFileUpload?: (file: File, uploadFor: string) => Promise<string>;
  setValue: UseFormSetValue<initialSchoolConfigProps>;
  userRole: string;
}

const OrgSections = ({
  control,
  sectionSchema,
  sectionName,
  userRole,
  setValue,
  handleFileUpload
}: OrgSectionProps) => {
  const watch = control._getWatch;
  const formData: initialSchoolConfigProps = watch();
  const { schoolClassesConfig } = useSelector(
    (state: RootState) => state.schoolConfig
  );

  const gradeOptions = (schoolClassesConfig?.grades || []).map(
    (grade: string) => ({
      label: grade,
      value: grade
    })
  );

  const renderParentsPostPublishAccess = () => {
    const parentPostPublishChildren = sectionSchema.filter(
      (sectionName) => sectionName.name === 'parentsPostPublishAccess'
    )[0].children;

    return parentPostPublishChildren?.map((publishAccess, index) => (
      <ParentPostPublishAccess
        key={`${publishAccess.name}-${index}`}
        publishAccess={publishAccess}
        control={control}
      />
    ));
  };

  const renderFeatureConfigs = () => {
    const featureConfigChildren = sectionSchema.filter(
      (sectionName) => sectionName.name === 'featureConfigs'
    )[0].children;

    const dynamicConfigCheckBox = featureConfigChildren?.filter(
      (featureKey) =>
        ![
          'featureConfigs.showStellarPaymentPostText.enabled',
          'featureConfigs.edutainFeed.enabled',
          'featureConfigs.enlightFeed.enabled',
          'featureConfigs.notesExplorer.enabled'
        ].includes(featureKey.name)
    );

    return (
      dynamicConfigCheckBox && (
        <FormRendererV2
          schema={dynamicConfigCheckBox as FormSchema}
          control={control}
          handleFileUpload={handleFileUpload}
          userRole={userRole}
        />
      )
    );
  };

  const renderFunctionalUserEmails = () => {
    return (
      <>
        <FormRendererV2
          schema={sectionSchema as FormSchema}
          control={control}
          handleFileUpload={handleFileUpload}
          userRole={userRole}
        />

        <Box marginTop={2}>
          <Accordion>
            <AccordionSummary expandIcon={<Icons.CaretDown />}>
              <Typography>Original Poster For Payment Post</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <CustomFieldMapping
                formData={formData}
                path='functionalUserEmails.originalPosterForPaymentPosts'
                onChange={(newValue) => {
                  setValue(
                    'functionalUserEmails.originalPosterForPaymentPosts',
                    newValue
                  );
                }}
                keyLabel='Section Name'
                valueLabel='Email'
                addButtonLabel='Add Section Mapping'
                isSourceAnObject
              />
            </AccordionDetails>
          </Accordion>
        </Box>
      </>
    );
  };

  const renderCustomFieldMapping = () => {
    const isGradeMapping = sectionName === 'crmToEtherGradeMappings';

    return (
      <CustomFieldMapping
        formData={formData}
        path={sectionName}
        onChange={(newValue) => setValue(sectionName as any, newValue)}
        keyLabel={isGradeMapping ? 'CRM Grade' : 'Field Name'}
        valueLabel={isGradeMapping ? 'Ether Grade' : 'Field Value'}
        addButtonLabel={
          isGradeMapping ? 'Add Grade Mapping' : 'Add Field Mapping'
        }
        isSourceAnObject={isGradeMapping ? false : true}
        valueOptions={isGradeMapping ? gradeOptions : undefined}
      />
    );
  };

  const renderBySection = () => {
    switch (sectionName) {
      case 'featureConfigs':
        return renderFeatureConfigs();

      case 'parentsPostPublishAccess':
        return renderParentsPostPublishAccess();

      case 'roleFeaturesMapping':
        return <RoleFeatureMapping control={control} setValue={setValue} />;

      case 'functionalUserEmails':
        return renderFunctionalUserEmails();

      case 'crmToEtherGradeMappings':
      case 'userCustomFieldMapping':
        return renderCustomFieldMapping();

      default:
        return (
          <FormRendererV2
            schema={sectionSchema as FormSchema}
            control={control}
            handleFileUpload={handleFileUpload}
            userRole={userRole}
            setValue={setValue}
          />
        );
    }
  };

  return <>{renderBySection()}</>;
};

export default OrgSections;
